/**
 * Estados financieros.
 *
 * Every figure here is folded out of the journal entries, which are themselves a projection of
 * the log. Nothing is stored and nothing is closed: a period is a filter, not a state the books
 * are put into.
 *
 * That last part is deliberate. Posting formal closing entries into an append-only log would
 * let two offline devices close the same month twice, and the second one would be impossible to
 * tell from a legitimate correction. Accumulated profit is therefore derived — income less cost
 * and expense over all time — rather than carried in `Resultados acumulados`.
 */

import { CUENTAS_BANCO, ZERO, sumarTotales, type Centavos } from '@abastece/shared';
import { ACCOUNTS, ACCOUNT_LIST, accountByCode, type AccountNature } from '../ledger/accounts.js';
import type { JournalEntry } from '../ledger/journal.js';

export interface Rango {
  /** Inclusive, `YYYY-MM-DD`. Omit for "since the beginning". */
  desde?: string;
  /** Inclusive, `YYYY-MM-DD`. Omit for "up to today". */
  hasta?: string;
}

export interface LineaReporte {
  codigo: string;
  nombre: string;
  monto: Centavos;
}

export function enRango(fecha: string, r: Rango): boolean {
  if (r.desde && fecha < r.desde) return false;
  if (r.hasta && fecha > r.hasta) return false;
  return true;
}

export function filtrar(entries: readonly JournalEntry[], r: Rango): JournalEntry[] {
  return entries.filter((e) => enRango(e.fecha, r));
}

/**
 * Per-account movement over a range, signed by the account's normal balance.
 *
 * A positive number always means "more of this": more cash, more debt, more expense. Accounts
 * with no movement are left out — a report listing forty zeroes is a report nobody reads.
 */
export function movimientos(entries: readonly JournalEntry[]): Map<string, Centavos> {
  const out = new Map<string, number>();
  for (const e of entries) {
    for (const l of e.lineas) {
      const acc = accountByCode(l.cuenta);
      if (!acc) continue;
      const delta = acc.normal === 'debe' ? l.debe - l.haber : l.haber - l.debe;
      out.set(l.cuenta, (out.get(l.cuenta) ?? 0) + delta);
    }
  }
  return new Map([...out].map(([k, v]) => [k, v as Centavos]));
}

function porNaturaleza(saldos: Map<string, Centavos>, naturaleza: AccountNature): LineaReporte[] {
  return ACCOUNT_LIST.filter((a) => a.nature === naturaleza)
    .map((a) => ({ codigo: a.code, nombre: a.name, monto: saldos.get(a.code) ?? ZERO }))
    .filter((l) => l.monto !== 0)
    .sort((a, b) => a.codigo.localeCompare(b.codigo));
}

const suma = (ls: readonly LineaReporte[]): Centavos =>
  ls.reduce<Centavos>((a, l) => sumarTotales(a, l.monto), ZERO);

// ---------------------------------------------------------------------------
// Estado de resultados
// ---------------------------------------------------------------------------

export interface EstadoResultados {
  rango: Rango;
  ingresos: LineaReporte[];
  totalIngresos: Centavos;
  costos: LineaReporte[];
  totalCostos: Centavos;
  /** Ventas menos costo de ventas: the number that says whether the pricing works. */
  utilidadBruta: Centavos;
  gastos: LineaReporte[];
  totalGastos: Centavos;
  utilidadNeta: Centavos;
  /** Gross margin in basis points. Null when nothing was sold. */
  margenBrutoBp: number | null;
}

export function estadoResultados(
  entries: readonly JournalEntry[],
  rango: Rango = {},
): EstadoResultados {
  const saldos = movimientos(filtrar(entries, rango));

  const ingresos = porNaturaleza(saldos, 'ingreso');
  const costos = porNaturaleza(saldos, 'costo');
  const gastos = porNaturaleza(saldos, 'gasto');

  const totalIngresos = suma(ingresos);
  const totalCostos = suma(costos);
  const totalGastos = suma(gastos);
  const utilidadBruta = sumarTotales(totalIngresos, -totalCostos);

  return {
    rango,
    ingresos,
    totalIngresos,
    costos,
    totalCostos,
    utilidadBruta,
    gastos,
    totalGastos,
    utilidadNeta: sumarTotales(utilidadBruta, -totalGastos),
    margenBrutoBp:
      totalIngresos > 0 ? Math.round((utilidadBruta * 10_000) / totalIngresos) : null,
  };
}

// ---------------------------------------------------------------------------
// Balance general
// ---------------------------------------------------------------------------

export interface BalanceGeneral {
  hasta: string | undefined;
  activos: LineaReporte[];
  totalActivos: Centavos;
  pasivos: LineaReporte[];
  totalPasivos: Centavos;
  patrimonio: LineaReporte[];
  /** Income less cost and expense over all time, up to the cut-off. Never posted, only derived. */
  utilidadAcumulada: Centavos;
  totalPatrimonio: Centavos;
  /** Activo − (Pasivo + Patrimonio). Must be zero; anything else means a reducer is broken. */
  descuadre: Centavos;
}

export function balanceGeneral(
  entries: readonly JournalEntry[],
  hasta?: string,
): BalanceGeneral {
  const alCorte = hasta ? entries.filter((e) => e.fecha <= hasta) : [...entries];
  const saldos = movimientos(alCorte);

  const activos = porNaturaleza(saldos, 'activo');
  const pasivos = porNaturaleza(saldos, 'pasivo');
  const patrimonio = porNaturaleza(saldos, 'patrimonio');

  const resultado = estadoResultados(alCorte);
  const totalActivos = suma(activos);
  const totalPasivos = suma(pasivos);
  const totalPatrimonio = sumarTotales(suma(patrimonio), resultado.utilidadNeta);

  return {
    hasta,
    activos,
    totalActivos,
    pasivos,
    totalPasivos,
    patrimonio,
    utilidadAcumulada: resultado.utilidadNeta,
    totalPatrimonio,
    descuadre: sumarTotales(totalActivos, -totalPasivos, -totalPatrimonio),
  };
}

// ---------------------------------------------------------------------------
// Libro de caja
// ---------------------------------------------------------------------------

/**
 * The accounts that hold money. A cash book that omits one of them is worse than none — money
 * handed over by a till and not yet counted (1130) included: it is cash in an envelope. So are
 * the dollars taken to the bank (1155): left out, a deposit read as money gone. And so is every
 * bank account the owner may open (`CUENTAS_BANCO`), taken from the chart rather than written out:
 * written out, the list kept only the first of each currency, and C$ 4,000 sent from one of the
 * owner's accounts to another read as C$ 4,000 gone.
 */
export const CUENTAS_EFECTIVO: readonly string[] = [
  ACCOUNTS.CAJA_CORDOBAS.code,
  ACCOUNTS.CAJA_DOLARES.code,
  ACCOUNTS.CAJA_FUERTE.code,
  ACCOUNTS.EFECTIVO_POR_RECIBIR.code,
  ...CUENTAS_BANCO.map((c) => ACCOUNTS[c].code),
  ACCOUNTS.BILLETERA_MOVIL.code,
];

export interface MovimientoCaja {
  fecha: string;
  eventId: string;
  concepto: string;
  cuenta: string;
  nombreCuenta: string;
  entrada: Centavos;
  salida: Centavos;
  /** Running balance across every cash account, in date order. */
  saldo: Centavos;
}

export interface LibroCaja {
  saldoInicial: Centavos;
  movimientos: MovimientoCaja[];
  totalEntradas: Centavos;
  totalSalidas: Centavos;
  saldoFinal: Centavos;
}

/**
 * Every movement of money, in order, with a running balance.
 *
 * Deliberately one book across all the cash accounts rather than one per account: the owner's
 * question is "how much money do we have and where did it go", and answering it once per account
 * is how the answer stops being checkable against the drawer.
 */
export function libroCaja(entries: readonly JournalEntry[], rango: Rango = {}): LibroCaja {
  const esEfectivo = (cuenta: string): boolean => CUENTAS_EFECTIVO.includes(cuenta);

  let saldoInicial = 0;
  if (rango.desde) {
    for (const e of entries) {
      if (e.fecha >= rango.desde) continue;
      for (const l of e.lineas) {
        if (esEfectivo(l.cuenta)) saldoInicial += l.debe - l.haber;
      }
    }
  }

  const ordenadas = filtrar(entries, rango).sort(
    (a, b) => a.fecha.localeCompare(b.fecha) || a.eventId.localeCompare(b.eventId),
  );

  const movimientos: MovimientoCaja[] = [];
  let saldo = saldoInicial;
  let totalEntradas = 0;
  let totalSalidas = 0;

  for (const e of ordenadas) {
    for (const l of e.lineas) {
      if (!esEfectivo(l.cuenta)) continue;
      saldo += l.debe - l.haber;
      totalEntradas += l.debe;
      totalSalidas += l.haber;
      movimientos.push({
        fecha: e.fecha,
        eventId: e.eventId,
        concepto: l.memo ? `${e.concepto} — ${l.memo}` : e.concepto,
        cuenta: l.cuenta,
        nombreCuenta: accountByCode(l.cuenta)?.name ?? l.cuenta,
        entrada: l.debe,
        salida: l.haber,
        saldo: saldo as Centavos,
      });
    }
  }

  return {
    saldoInicial: saldoInicial as Centavos,
    movimientos,
    totalEntradas: totalEntradas as Centavos,
    totalSalidas: totalSalidas as Centavos,
    saldoFinal: saldo as Centavos,
  };
}
